import { apiRequest } from '../api';
import { state } from '../state';
import { t } from '../i18n';

// ============================================================
// Подсказки для админа: кто именно поставил реакции/голоса и кто подписан.
// Данные грузятся лениво при наведении и кэшируются на сессию.
// ============================================================

export type WhoEntity = 'player' | 'map' | 'mod' | 'game';

export interface WhoPerson {
  username: string;
  at: string | null;
  player_name?: string | null;
}

export interface WhoPayload {
  entity: WhoEntity;
  id: number;
  reactions: Record<string, WhoPerson[]>;
  votes: { up: WhoPerson[]; down: WhoPerson[] };
  followers: WhoPerson[];
}

const cache = new Map<string, Promise<WhoPayload | null>>();

export function isAdminViewer(): boolean {
  return Boolean(state.user?.is_admin);
}

/** Имена через запятую (без пустых). */
export function whoNames(list: WhoPerson[] | undefined): string {
  const names = (list ?? []).map((p) => p.username).filter(Boolean);
  return names.length > 0 ? names.join(', ') : t('who.none');
}

/** Загружает подсказку (кэшируется); null — если нет прав или таблицы нет. */
export function loadWho(entity: WhoEntity, id: number): Promise<WhoPayload | null> {
  if (!isAdminViewer() || !state.token) return Promise.resolve(null);

  const key = `${entity}:${id}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const promise = apiRequest<WhoPayload>(
    `/api/admin/who?entity=${entity}&id=${id}`,
    { token: state.token },
  ).catch(() => null);

  cache.set(key, promise);
  return promise;
}

/**
 * Добавляет к элементу админскую подсказку при наведении.
 * build получает загруженные данные и возвращает текст (или null — без подсказки).
 * Базовый title (если был) сохраняется первой строкой.
 */
export function attachWhoTooltip(
  el: HTMLElement,
  entity: WhoEntity,
  id: number,
  build: (payload: WhoPayload) => string | null,
): void {
  let baseTitle: string | null = null;
  let applied = false;

  el.addEventListener('mouseenter', () => {
    if (baseTitle === null) baseTitle = el.title;
    void loadWho(entity, id).then((payload) => {
      if (!payload) return;
      const extra = build(payload);
      if (!extra) return;
      el.title = baseTitle ? `${baseTitle}\n${extra}` : extra;
      applied = true;
    });
  });

  el.addEventListener('mouseleave', () => {
    if (!applied) return;
    el.title = baseTitle ?? '';
    applied = false;
  });
}
